'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, LogIn, Pencil } from 'lucide-react';
import {
  FormFields,
  missingRequired,
  type FormAnswers,
  type WebForm,
} from '@church/nextjs-ui/website/forms';
import { ApiError, fetchForm, is404, submitForm } from '@/lib/api';
import {
  getMpWidgetToken,
  getSelectedCampusId,
  onMpTokenChange,
  signInAndReturn,
} from '@/lib/campus';
import { clearDraft, loadDraft, saveDraft } from '@/lib/draft';
import { formatPhone, isPhone } from '@/lib/phone';
import {
  campusFields,
  campusFromPath,
  matchOption,
  presetAnswers,
  type PresetInput,
} from '@/lib/presets';
import type { FormResponse } from '@/lib/types';

/**
 * One MP custom form: who you are, the questions, one Send. A signed-in
 * visitor is known already, so their details show as a line they can edit
 * rather than four empty boxes; everyone else types them, or signs in to
 * skip that. The campus question, when a form has one, is the campus picker
 * and answers itself from whatever the page already knows.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Person {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const EMPTY: Person = { firstName: '', lastName: '', email: '', phone: '' };

export interface CustomFormProps {
  apiBaseUrl: string;
  formRef: string | null;
  /** `true` shows the form's own MP title, `false` shows none, a string replaces it. */
  heading: boolean | string;
  /** Answers the host page already knows (campus, ministry, explicit field ids). */
  presets?: PresetInput;
  /** MP's own widget always required a mobile number; `data-phone="optional"` relaxes it. */
  phoneRequired?: boolean;
  submitLabel?: string | null;
}

export function CustomForm({
  apiBaseUrl,
  formRef,
  heading,
  presets,
  phoneRequired = true,
  submitLabel,
}: CustomFormProps) {
  const [data, setData] = useState<FormResponse | null>(null);
  const [error, setError] = useState<'missing' | 'failed' | null>(null);
  const [token, setToken] = useState<string | null>(() => getMpWidgetToken());
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [person, setPerson] = useState<Person>(EMPTY);
  const [editingPerson, setEditingPerson] = useState(false);
  const [website, setWebsite] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [done, setDone] = useState<{ completeMessage: string | null } | null>(null);
  const [touched, setTouched] = useState(false);
  const formEl = useRef<HTMLFormElement | null>(null);

  useEffect(() => onMpTokenChange(setToken), []);

  useEffect(() => {
    if (!formRef) {
      setError('missing');
      return;
    }
    const ctrl = new AbortController();
    setError(null);
    fetchForm(apiBaseUrl, formRef, ctrl.signal)
      .then((res) => {
        setData(res);
        if (res.viewer) {
          setPerson((p) => ({
            firstName: p.firstName || res.viewer!.firstName,
            lastName: p.lastName || res.viewer!.lastName,
            email: p.email || res.viewer!.email || '',
            phone: p.phone || formatPhone(res.viewer!.phone || ''),
          }));
        }

        const campusNames = res.campuses.map((c) => c.name);
        // Everything the page and the visitor already told us, weakest signal
        // first so the firmest wins: a draft they were part way through, the
        // site header's campus, their household campus from MP, the campus
        // this page is about, and last the answers the embed named outright.
        const start: FormAnswers = { ...(loadDraft(res.form.id) ?? {}) };

        const cookieCampus = getSelectedCampusId();
        const fromCookie = cookieCampus
          ? (res.campuses.find((c) => c.id === cookieCampus)?.name ?? null)
          : null;
        const fromHousehold = res.viewer?.homeCampusName ?? null;
        const fromPath = campusFromPath(window.location.pathname, campusNames);
        for (const name of [fromCookie, fromHousehold, fromPath, presets?.campus?.trim() || null]) {
          if (!name) continue;
          for (const field of campusFields(res.form, campusNames)) {
            const option = matchOption(field, name);
            if (option) start[String(field.id)] = option;
          }
        }

        Object.assign(start, presetAnswers(res.form, campusNames, presets ?? {}));
        setAnswers((current) => (Object.keys(current).length ? current : start));
      })
      .catch((err) => {
        if (ctrl.signal.aborted) return;
        setError(is404(err) ? 'missing' : 'failed');
      });
    return () => ctrl.abort();
    // presets come from the embed and never change after boot
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, formRef, token]);

  const form: WebForm | null = data?.form ?? null;

  // Keep what they have typed, so a stray tap on a link does not cost them the form.
  useEffect(() => {
    if (form && !done) saveDraft(form.id, answers);
  }, [form, answers, done]);

  const missing = useMemo(() => (form ? missingRequired(form, answers) : []), [form, answers]);
  const missingIds = useMemo(() => new Set(missing.map((f) => f.id)), [missing]);
  const personProblems = useMemo(() => {
    const out: Partial<Record<keyof Person, string>> = {};
    if (!person.firstName.trim()) out.firstName = 'Needed';
    if (!person.lastName.trim()) out.lastName = 'Needed';
    if (!EMAIL.test(person.email.trim())) out.email = person.email.trim() ? 'Check this' : 'Needed';
    if (phoneRequired && !isPhone(person.phone)) {
      out.phone = person.phone.trim() ? 'Check this' : 'Needed';
    }
    return out;
  }, [person, phoneRequired]);
  const personOk = Object.keys(personProblems).length === 0;
  const canSend = Boolean(
    form && !form.closed && form.supported && personOk && missing.length === 0
  );

  /** Put the first thing that needs attention on screen, rather than naming it and leaving. */
  const showFirstProblem = () => {
    const root = formEl.current;
    if (!root) return;
    const target = !personOk
      ? root.querySelector<HTMLElement>('[data-person-invalid="true"]')
      : root.querySelector<HTMLElement>(`[data-field-id="${missing[0]?.id}"]`);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.querySelector('input, textarea, select, button')?.dispatchEvent(new Event('focus'));
  };

  const send = async () => {
    if (!form || !formRef) return;
    setTouched(true);
    if (!canSend) {
      if (!personOk) setEditingPerson(true);
      requestAnimationFrame(showFirstProblem);
      return;
    }
    setSending(true);
    setSendError(null);
    try {
      const res = await submitForm(apiBaseUrl, formRef, {
        person: {
          firstName: person.firstName.trim(),
          lastName: person.lastName.trim(),
          email: person.email.trim(),
          phone: person.phone.trim(),
        },
        answers,
        website,
      });
      clearDraft(form.id);
      setDone({ completeMessage: res.completeMessage ?? form.completeMessage });
    } catch (err) {
      // The sign-in ran out and MP would not renew it: open the sign-in, not an error.
      if (err instanceof ApiError && err.status === 401) {
        setSendError(null);
        signInAndReturn();
      } else
        setSendError(
          err instanceof Error ? err.message : 'Something went wrong. Please try again.'
        );
    } finally {
      setSending(false);
    }
  };

  if (error === 'missing') {
    return (
      <Frame>
        <p className="text-navy font-bold">This form is not available.</p>
        <p className="text-slate mt-1 text-sm">
          The link may be out of date. If you were sent here, let the office know.
        </p>
      </Frame>
    );
  }
  if (error === 'failed') {
    return (
      <Frame>
        <p className="text-navy font-bold">The form could not load.</p>
        <p className="text-slate mt-1 text-sm">Please try again in a moment.</p>
      </Frame>
    );
  }
  if (!form) {
    return (
      <Frame>
        <div className="space-y-4" aria-busy="true">
          <div className="skeleton h-5 w-1/3" />
          <div className="skeleton h-11 w-full" />
          <div className="skeleton h-11 w-full" />
          <div className="skeleton h-24 w-full" />
        </div>
      </Frame>
    );
  }

  const title = heading === false ? undefined : heading === true ? form.title : heading;

  if (done) {
    return (
      <Frame>
        <div className="rise flex items-start gap-3" role="status">
          <span className="bg-teal flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white">
            <Check className="h-5 w-5" strokeWidth={3} />
          </span>
          <div className="min-w-0">
            <p className="text-navy text-lg font-extrabold">
              Thank you, {person.firstName.trim()}.
            </p>
            {done.completeMessage ? (
              <div
                className="mp-html text-slate-deep mt-1 text-sm"
                dangerouslySetInnerHTML={{ __html: done.completeMessage }}
              />
            ) : (
              <p className="text-slate-deep mt-1 text-sm">
                We have your response, and someone will follow up.
              </p>
            )}
            <button
              type="button"
              className="text-teal-deep mt-3 text-xs font-bold hover:underline"
              onClick={() => {
                setAnswers({});
                setDone(null);
                setTouched(false);
              }}
            >
              Send another response
            </button>
          </div>
        </div>
      </Frame>
    );
  }

  if (form.closed) {
    return (
      <Frame title={title}>
        <p className="text-navy font-bold">This form is no longer taking responses.</p>
      </Frame>
    );
  }
  if (!form.supported) {
    return (
      <Frame title={title}>
        <p className="text-navy font-bold">This form cannot be completed online yet.</p>
        <p className="text-slate mt-1 text-sm">
          It asks for a file upload. Please contact the office.
        </p>
      </Frame>
    );
  }

  const viewer = data?.viewer ?? null;
  const showPersonFields = !viewer || editingPerson;
  const campuses = data?.campuses ?? [];

  return (
    <Frame title={title}>
      <form
        ref={formEl}
        className="space-y-6"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        {form.instructions && (
          <div
            className="mp-html text-slate-deep -mt-2 text-sm"
            dangerouslySetInnerHTML={{ __html: form.instructions }}
          />
        )}

        <section>
          <h3 className="kicker mb-3">About you</h3>
          {viewer && !editingPerson ? (
            <div className="border-line flex items-center gap-3 rounded-2xl border px-4 py-3">
              <Avatar name={`${person.firstName} ${person.lastName}`} image={viewer.image} />
              <div className="min-w-0 flex-1">
                <div className="text-navy truncate text-sm font-bold">
                  {person.firstName} {person.lastName}
                </div>
                <div className="text-slate truncate text-xs">
                  {[person.email, person.phone].filter(Boolean).join(' · ')}
                </div>
              </div>
              <button
                type="button"
                className="chip shrink-0"
                onClick={() => setEditingPerson(true)}
                aria-label="Edit your details"
              >
                <Pencil className="h-3 w-3" /> Edit
              </button>
            </div>
          ) : null}
          {showPersonFields && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label="First name"
                value={person.firstName}
                onChange={(v) => setPerson({ ...person, firstName: v })}
                autoComplete="given-name"
                required
                problem={touched ? personProblems.firstName : undefined}
              />
              <Input
                label="Last name"
                value={person.lastName}
                onChange={(v) => setPerson({ ...person, lastName: v })}
                autoComplete="family-name"
                required
                problem={touched ? personProblems.lastName : undefined}
              />
              <Input
                label="Email"
                type="email"
                value={person.email}
                onChange={(v) => setPerson({ ...person, email: v })}
                autoComplete="email"
                required
                problem={touched ? personProblems.email : undefined}
              />
              <Input
                label="Mobile phone"
                type="tel"
                value={person.phone}
                onChange={(v) => setPerson({ ...person, phone: formatPhone(v) })}
                autoComplete="tel"
                required={phoneRequired}
                problem={touched ? personProblems.phone : undefined}
              />
            </div>
          )}
          {!viewer && (
            <button
              type="button"
              className="text-teal-deep mt-3 inline-flex items-center gap-1.5 text-xs font-bold hover:underline"
              onClick={signInAndReturn}
            >
              <LogIn className="h-3.5 w-3.5" /> Sign in to fill this in for you
            </button>
          )}
          {/* Honeypot: real people never see it. */}
          <label className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
            Website
            <input
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </label>
        </section>

        {form.fields.some((f) => !f.hidden && f.parentId === null) && (
          <section>
            <FormFields
              form={form}
              answers={answers}
              onChange={setAnswers}
              campuses={campuses}
              invalidIds={touched ? missingIds : undefined}
            />
          </section>
        )}

        {touched && !canSend && (
          <p className="text-rose text-sm font-semibold" role="alert">
            {!personOk
              ? 'Please check your name, email and phone above.'
              : `Still needed: ${missing.map((f) => f.title ?? f.label).join(', ')}.`}
          </p>
        )}
        {sendError && (
          <p className="text-rose text-sm font-semibold" role="alert">
            {sendError}
          </p>
        )}

        <div className="flex items-center justify-end gap-3">
          <button
            type="submit"
            className="btn btn-primary w-full min-w-32 sm:w-auto"
            disabled={sending}
            aria-disabled={!canSend}
          >
            {sending ? 'Sending…' : submitLabel?.trim() || 'Send'}
          </button>
        </div>
      </form>
    </Frame>
  );
}

function Frame({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="card relative p-5 sm:p-7">
      {title && <h2 className="text-navy mb-4 text-2xl font-extrabold tracking-tight">{title}</h2>}
      {children}
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
  required,
  problem,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  problem?: string;
}) {
  return (
    <label className="block scroll-mt-24" data-person-invalid={problem ? 'true' : undefined}>
      <span className="text-navy mb-1 block text-[0.85rem] font-bold">
        {label}
        {required && <span className="text-rose ml-1">*</span>}
        {problem && <span className="text-rose ml-2 text-xs font-semibold">{problem}</span>}
      </span>
      <input
        className={`field ${problem ? '!border-rose ring-rose/20 ring-2' : ''}`}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        aria-invalid={problem ? true : undefined}
      />
    </label>
  );
}

function Avatar({ name, image }: { name: string; image: string | null }) {
  const [broken, setBroken] = useState(false);
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const text = (parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '');
  if (image && !broken) {
    return (
      <img
        src={image}
        alt=""
        className="h-10 w-10 shrink-0 rounded-full object-cover"
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <span className="bg-navy flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-extrabold text-white">
      {text.toUpperCase() || '·'}
    </span>
  );
}
